import classNames from 'classnames';
import map from 'lodash/fp/map';
import concat from 'lodash/fp/concat';
import flow from 'lodash/fp/flow';
import uniqBy from 'lodash/fp/uniqBy';
import sortBy from 'lodash/fp/sortBy';
import groupBy from 'lodash/fp/groupBy';
import mapValues from 'lodash/fp/mapValues';
import PropTypes from 'prop-types';
import React from 'react';
import ReactSelect from 'react-select';

import { Department } from 'join-us/components/Department';

const ALL_OFFICES = 'All Locations';
const ALL_DEPARTMENTS = 'All Departments';

export class JobBoard extends React.Component {
  static propTypes = {
    jobs: PropTypes.array.isRequired,
  };

  state = {
    filter: '',
    filterRegex: undefined,
    officeId: ALL_OFFICES,
    departmentId: ALL_DEPARTMENTS,
  };

  handleOfficeChange = option => {
    this.setState({ officeId: option.value });
  };

  handleDepartmentChange = option => {
    this.setState({ departmentId: option.value });
  };

  handleFilterChange = event => {
    const filter = event.target.value;

    this.setState({
      filter,
      filterRegex: new RegExp(filter.trim().toLowerCase(), 'i'),
    });
  };

  isJobMatchingFilters = (job, { officeId, departmentId, filterRegex }) => {
    // The job matches the text filter regex.
    const matchesFilter =
      !filterRegex ||
      filterRegex.test(job.title) ||
      filterRegex.test(job.location.name);

    // The job matches the specified office.
    const matchesOffice =
      officeId === ALL_OFFICES || officeId === job.office.id;

    // The job matches the specified department.
    const matchesDepartment =
      departmentId === ALL_DEPARTMENTS || departmentId === job.department.id;

    return matchesFilter && matchesOffice && matchesDepartment;
  };

  filterJobs(jobs, { officeId, departmentId, filterRegex }) {
    return jobs.filter(job =>
      this.isJobMatchingFilters(job, { officeId, departmentId, filterRegex })
    );
  }

  /**
   * Returns options for a given filter
   * @param jobs [Array] Array of jobs
   * @param filterKey [String] The target key (office or department)
   * @param defaultFilterValue [String] The default value for the target
   * @return [Array] Array of options
   */
  getFilterOptionsForJobs(jobs, filterKey, defaultFilterValue) {
    const mapJobToOptions = map(job => ({
      label: job[filterKey].name,
      value: job[filterKey].id,
    }));
    const concatToDefaults = concat({
      label: defaultFilterValue,
      value: defaultFilterValue,
    });

    return flow(
      mapJobToOptions,
      uniqBy('value'),
      sortBy('label'),
      concatToDefaults
    )(jobs);
  }

  groupJobsByDepartment(jobs) {
    const mapJobGroupsToDepartments = mapValues(jobGroup => ({
      id: jobGroup[0].department.id,
      name: jobGroup[0].department.name,
      jobs: uniqBy('id')(jobGroup),
    }));

    return flow(
      groupBy('department.id'),
      mapJobGroupsToDepartments,
      sortBy('name')
    )(jobs);
  }

  renderDepartment(department) {
    return <Department key={department.id} department={department} />;
  }

  renderJobs(jobs) {
    const departments = this.groupJobsByDepartment(jobs);
    return departments.map(this.renderDepartment);
  }

  renderSelect({ className, ...props }) {
    return (
      <ReactSelect
        className={classNames('JobBoard__select', className)}
        clearable={false}
        searchable={false}
        {...props}
      />
    );
  }

  render() {
    const { filterRegex, officeId, departmentId } = this.state;

    const filteredJobs = this.filterJobs(this.props.jobs, {
      filterRegex,
      officeId,
      departmentId,
    });

    const officeOptions = this.getFilterOptionsForJobs(
      filteredJobs,
      'office',
      ALL_OFFICES
    );

    const departmentOptions = this.getFilterOptionsForJobs(
      filteredJobs,
      'department',
      ALL_DEPARTMENTS
    );

    return (
      <div>
        <div className="JobBoard__controls">
          <input
            className="JobBoard__filter-input"
            onChange={this.handleFilterChange}
            placeholder="Search"
            type="text"
            value={this.state.filter}
          />
          {this.renderSelect({
            className: 'JobBoard__office-select',
            value: this.state.officeId,
            options: officeOptions,
            onChange: this.handleOfficeChange,
          })}
          {this.renderSelect({
            className: 'JobBoard__department-select',
            value: this.state.departmentId,
            options: departmentOptions,
            onChange: this.handleDepartmentChange,
          })}
        </div>
        <div className="JobBoard__content">{this.renderJobs(filteredJobs)}</div>
      </div>
    );
  }
}
